import React from 'react';
import CompletionBar from '../CompletionBar';
import taxonomyConfig from '../../../constants/taxonomyConfig.json';
import sortSelectOptions from '../../../utils/sortSelectOptions';
import { champsManquants } from '../reglesEtapes';
import ChampsManquants from '../ChampsManquants';
import BarreEtape from '../BarreEtape';

const Step1_Taxonomy = ({ data, updateData, nextStep }) => {
  
  const handleChange = (field, value) => {
    if (field === 'operation') {
      updateData({ operation: value, objet: '', profil: '', sous_profil: '', cible: '' });
    } else if (field === 'objet') {
      updateData({ objet: value, profil: '', sous_profil: '', cible: '' });
    } else if (field === 'profil') {
      updateData({ profil: value, sous_profil: '', cible: '' });
    } else {
      updateData({ [field]: value });
    }
  };

  const getObjets = () => {
    if (!data.operation || !taxonomyConfig.operations[data.operation]) return {};
    const objets = taxonomyConfig.operations[data.operation].objets || {};
    // Masquer les objets marqués hidden:true (ex: parts_sociales)
    return Object.fromEntries(
      Object.entries(objets).filter(([, obj]) => !obj.hidden)
    );
  };

  const getProfils = () => {
    const objets = getObjets();
    if (!data.objet || !objets[data.objet]) return {};
    return objets[data.objet].profils || {};
  };

  const getSousProfils = () => {
    const profils = getProfils();
    if (!data.profil || !profils[data.profil]) return {};
    return profils[data.profil].sous_profils || {};
  };

  const _isAutreSp = data.sous_profil && String(data.sous_profil).toLowerCase().includes('autre');
  // iter102 : le profil neutre `autre` (« Autre / toutes activités ») ne DOIT PAS
  // exiger de précision — c'est justement fait pour les locaux vides / sans activité.
  // La règle reste bloquante pour `autre_chr`, `autre_retail`, etc. sur un profil réel.
  const _isProfilNeutre = data.profil === 'autre';
  const _needActivitePrecise = _isAutreSp && !_isProfilNeutre;
  const _hasActivitePrecise = !!(data.activite_precise || data.activite_autre);
  const isComplete = data.operation && data.objet && data.profil && data.sous_profil && (!_needActivitePrecise || _hasActivitePrecise);

  return (
    <div className="h-full flex flex-col">
      <CompletionBar data={data} currentStep={1} />

      <div className="mb-3 mt-3">
        <h2 className="text-lg font-bold text-gray-900 mb-0.5">Classification du Bien</h2>
        <p className="text-xs text-gray-600">Informations obligatoires. <span className="text-red-500 font-bold">*</span></p>
      </div>

      <div className="flex-1 space-y-3">
        {/* Ligne 1: Type de transaction + Type de bien */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Type de transaction <span className="text-red-500">*</span></label>
            <select
              value={data.operation}
              onChange={(e) => handleChange('operation', e.target.value)}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 bg-white"
            >
              <option value="">Sélectionner...</option>
              {Object.entries(taxonomyConfig.operations).map(([key, op]) => (
                <option key={key} value={key}>{op.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Type de bien <span className="text-red-500">*</span></label>
            <select
              value={data.objet}
              onChange={(e) => handleChange('objet', e.target.value)}
              disabled={!data.operation}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 bg-white disabled:bg-gray-100"
            >
              <option value="">Sélectionner...</option>
              {sortSelectOptions(Object.entries(getObjets())).map(([key, obj]) => (
                <option key={key} value={key}>{obj.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Ligne 2: Catégorie + Activité précise */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Catégorie <span className="text-red-500">*</span></label>
            <select
              value={data.profil}
              onChange={(e) => handleChange('profil', e.target.value)}
              disabled={!data.objet}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 bg-white disabled:bg-gray-100"
            >
              <option value="">Sélectionner...</option>
              {sortSelectOptions(Object.entries(getProfils())).map(([key, profil]) => (
                <option key={key} value={key}>{profil.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Activité précise <span className="text-red-500">*</span></label>
            <select
              value={data.sous_profil}
              onChange={(e) => handleChange('sous_profil', e.target.value)}
              disabled={!data.profil}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 bg-white disabled:bg-gray-100"
            >
              <option value="">Sélectionner...</option>
              {/* Sauf pour "licence" (ordinal III/IV → NON trié) */}
              {(data.profil === 'licence'
                ? Object.entries(getSousProfils())
                : sortSelectOptions(Object.entries(getSousProfils()))
              ).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Ligne 3: Cible (TOUJOURS VISIBLE) */}
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">Cible</label>
          <select
            value={data.cible}
            onChange={(e) => handleChange('cible', e.target.value)}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 bg-white"
          >
            <option value="">Non spécifié</option>
            <option value="investisseur">Investisseur</option>
            <option value="utilisateur">Utilisateur</option>
            <option value="indifferent">Indifférent</option>
          </select>
        </div>

        {/* Libellé d'affichage (activite_precise) — TOUJOURS visible.
            iter102 : trois modes selon la taxonomie :
              (a) profil `autre` (« Autre / toutes activités ») → optionnel « Activité pressentie »
                  (locaux vides sans activité déterminée).
              (b) sous_profil `autre_*` sous un profil réel (chr, retail, licence…) → OBLIGATOIRE
                  (« Précisez l'activité »).
              (c) sinon → libellé optionnel qui remplace le libellé standard. */}
        {(() => {
          const isProfilNeutre = data.profil === 'autre';
          const isAutreSp = data.sous_profil && data.sous_profil.toLowerCase().includes('autre');
          const isRequired = isAutreSp && !isProfilNeutre;
          const usesActiviteChannel = isAutreSp;
          const currentValue = data.activite_precise || (usesActiviteChannel ? (data.activite_autre || '') : '');
          const wrapperCls = isRequired ? 'bg-yellow-50 border border-yellow-200 rounded p-3' : '';
          const label = isRequired
            ? "Précisez l'activité"
            : (isProfilNeutre ? "Activité pressentie" : "Libellé d'affichage");
          const placeholder = isProfilNeutre
            ? "Ex : Toutes activités, Tous commerces sauf restauration, Idéal tertiaire…"
            : (isRequired ? "Ex : Salon de thé, Agence immobilière…" : "Ex : Brasserie coffee shop");
          const helperText = isProfilNeutre
            ? "Optionnel — décrit le potentiel du local sans le limiter à une activité fixe. S'affiche en priorité (fiche, PDF, listings)."
            : (!isRequired ? "Ce libellé remplace l'activité affichée (fiche publique, PDF, listings). Les statistiques restent basées sur la catégorie sélectionnée." : null);
          return (
            <div className={wrapperCls}>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                {label}
                {isRequired && <span className="text-red-500"> *</span>}
                {!isRequired && <span className="text-slate-500 font-normal ml-1">(optionnel)</span>}
              </label>
              <input
                type="text"
                data-testid="wizard-activite-precise-input"
                value={currentValue}
                onChange={(e) => {
                  const v = e.target.value;
                  if (usesActiviteChannel) {
                    updateData({ activite_precise: v, activite_autre: v });
                  } else {
                    updateData({ activite_precise: v });
                  }
                }}
                placeholder={placeholder}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
              />
              {helperText && (
                <p className="text-[10px] text-slate-500 mt-1">{helperText}</p>
              )}
            </div>
          );
        })()}
      </div>

      {/* lot Z2 — dire QUOI remplir plutôt que laisser un bouton grisé muet.
          « Précisez l'activité » n'est exigé que par cette étape (pas par la règle de
          passage) : il est ajouté ici pour que la liste corresponde au bouton. */}
      <BarreEtape
        manquants={(
          <ChampsManquants
            champs={[
              ...champsManquants(2, data),
              ...(_needActivitePrecise && !_hasActivitePrecise ? ["Précisez l'activité"] : []),
            ]}
            testId="wizard-step1-champs-manquants"
          />
        )}
      >
        <span className="hidden md:block" />
        <button
          onClick={nextStep}
          disabled={!isComplete}
          data-testid="wizard-step1-next"
          className="h-12 md:h-auto px-5 md:py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-sm font-semibold rounded transition-colors"
        >
          Suivant <i className="bi bi-arrow-right"></i>
        </button>
      </BarreEtape>
    </div>
  );
};

export default Step1_Taxonomy;